import { ROUTES } from '@configs/routes';
import { LocalStorageKeys } from '@constants/enums';
import { INPUT_TYPES, PLACEHOLDERS } from '@constants/inputs.constants';
import ConfirmCode from '@efex-components/TwoFactorCode/ConfirmCode';
import GlobalLoader from '@efex-components/common/GlobalLoader/GlobalLoader';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from '@efexteam/components';
import useReturnUrl from '@main-hooks/useReturnUrl';
import RefreshIcon from '@mui/icons-material/Refresh';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { loginEn, loginEs } from '../../assets';
import { PasswordResetModal } from '../../components/login/PasswordResetModal';
import LangSelector from '../../components/navbar/lang';
import LogoController from './components/LogoController/LogoController';
import useLoginService from './hooks/useLogin.service';
import { LoginProps } from './types';

const LoginLayout = () => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const navigate = useNavigate();

  const { navigateToReturnUrl } = useReturnUrl();

  const {
    success,
    messageData,
    successMessage,
    lgResp,
    isUserLogged,
    showPassword,
    showTwoFactorValidation,
    showPasswordResetModal,
    isLoginProcess,
    formik,
    searchParams,
    isWhiteLabel,
    whiteLabelState,
    handleClickShowPassword,
    goToRegisterPage,
    handleResetPassword,
    toggleTwoFactor,
    togglePasswordResetModal,
    validateUserData,
  } = useLoginService();

  const handlePasswordResetSent = (email: string) => {
    localStorage.setItem(LocalStorageKeys.RESET_EMAIL_PASSWORD, email);

    navigate(ROUTES.AUTH.PASSWORD_MIGRATION, {
      state: {
        message: t('resetPasswordSuccessMessage') || '',
        email,
        isSuccessMessage: true,
      },
    });
  };

  useEffect(() => {
    if (isUserLogged) {
      navigateToReturnUrl();
    }
  }, [isUserLogged, navigateToReturnUrl]);

  if (isUserLogged) {
    return null;
  }

  if (typeof isWhiteLabel === 'undefined') return <GlobalLoader />;

  return (
    <Box className="min-h-screen relative">
      <Box className="min-h-[70px] absolute top-0 left-0 right-0 flex justify-between items-center z-10">
        <Box className={clsx('absolute left-0', isWhiteLabel && 'm-3')}>
          <LogoController
            isWhiteLabel={!!whiteLabelState.organizationID}
            efexLogoProps={{ className: 'max-w-[150px] ml-4 mt-2' }}
            whiteLabelLogo={whiteLabelState.organizationLogoURL}
          />
        </Box>
        <Box
          className={clsx('absolute right-0 pt-2 pr-2', isWhiteLabel && 'm-3')}
        >
          <LangSelector />
        </Box>
      </Box>
      <Grid2 container className="h-screen">
        <Grid2
          component="div"
          size={{ xs: 12, md: isWhiteLabel ? 12 : 6 }}
          position="relative"
          className={isWhiteLabel ? 'flex justify-center' : ''}
        >
          <Box
            className={`flex items-center justify-center h-full px-8 py-20 md:px-20 ${
              isWhiteLabel ? 'w-full max-w-2xl' : ''
            }`}
          >
            <Paper elevation={0} className="w-full">
              <Typography variant="h4" fontWeight={600} fontSize={32} mb={4}>
                {t(isWhiteLabel ? 'lngWelcomeWhiteLabel' : 'lgnWelcomeEfex')}
              </Typography>

              <form onSubmit={formik.handleSubmit}>
                <Box mb={3}>
                  <TextField
                    fullWidth
                    type={INPUT_TYPES.TEXT}
                    id="email"
                    name="email"
                    label="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && !!formik.errors.email}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Box>

                <Box mb={4}>
                  <Box position="relative">
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      label={t('password')}
                      type={
                        showPassword ? INPUT_TYPES.TEXT : INPUT_TYPES.PASSWORD
                      }
                      placeholder={
                        showPassword
                          ? (t('password') as string)
                          : PLACEHOLDERS.PASSWORD_MASK
                      }
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.password && !!formik.errors.password
                      }
                      helperText={
                        formik.touched.password && formik.errors.password
                      }
                      slotProps={{
                        input: {
                          endAdornment: (
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        },
                      }}
                    />
                  </Box>
                  <Box
                    className="flex flex-row items-center justify-end my-3"
                    fontSize={12}
                  >
                    <Typography variant="caption" component="span">
                      {t('lngPasswordForgot')}
                    </Typography>
                    <Button
                      appearance="primary"
                      size="small"
                      className="!inline-block !min-w-[auto]"
                      variant="text"
                      onClick={handleResetPassword}
                    >
                      <span className="underline">{t('clickHere')}</span>
                    </Button>
                  </Box>
                </Box>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    appearance="secondary"
                    onClick={goToRegisterPage}
                    type="button"
                    size="large"
                  >
                    {t('register')}
                  </Button>
                  <Button
                    appearance="primary"
                    className={clsx(
                      isLoginProcess ? '!bg-slate-300 !pointer-events-none' : ''
                    )}
                    type="submit"
                    disabled={isLoginProcess}
                    size="large"
                  >
                    {isLoginProcess ? (
                      <RefreshIcon className="animate-spin" />
                    ) : null}
                    {t('login')}
                  </Button>
                </Stack>

                {success && !isLoginProcess && (
                  <Typography
                    className="text-center"
                    fontSize={12}
                    color="#56c036"
                    mt={4}
                  >
                    {t('loginSuccess')}
                  </Typography>
                )}

                {messageData && (
                  <Typography
                    className="text-center"
                    fontSize={12}
                    color="error"
                    mt={4}
                  >
                    {t(messageData)}
                  </Typography>
                )}

                {successMessage && (
                  <Typography
                    className="text-center"
                    fontSize={12}
                    color="success.main"
                    mt={4}
                  >
                    {successMessage}
                  </Typography>
                )}

                {searchParams.get('action') === 'reset' && (
                  <Typography
                    className="text-center"
                    fontSize={12}
                    color="#56c036"
                    mt={4}
                  >
                    {t('resetSuccessEmail')}
                  </Typography>
                )}
              </form>
            </Paper>
          </Box>
        </Grid2>

        {!isWhiteLabel && (
          <Grid2
            size={{ xs: 12, md: 6 }}
            className="bg-[#EBF3FA] hidden md:block"
            sx={{
              backgroundImage: `url(${language === 'es' ? loginEs : loginEn})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}
      </Grid2>

      {showTwoFactorValidation ? (
        <Dialog open={showTwoFactorValidation} onClose={toggleTwoFactor}>
          <DialogTitle
            className="!mb-5"
            showCloseButton
            onClick={toggleTwoFactor}
          >
            <Stack justifyContent="center" alignItems="center">
              <LogoController
                isWhiteLabel={!!whiteLabelState.organizationID}
                efexLogoProps={{ className: 'mb-2 max-w-[100px]' }}
                whiteLabelLogo={whiteLabelState.organizationLogoURL}
              />
              {t('tfTitle') || ''}
            </Stack>
          </DialogTitle>
          <DialogContent>
            <ConfirmCode
              nextStep={(values) =>
                validateUserData({
                  ...(lgResp as LoginProps),
                  loginResponse: values,
                })
              }
              isValidationStep
              show90DaysCheckbox
            />
          </DialogContent>
        </Dialog>
      ) : null}

      <PasswordResetModal
        open={showPasswordResetModal}
        onClose={togglePasswordResetModal}
        userEmail={formik.values.email}
        onPasswordResetSent={handlePasswordResetSent}
      />
    </Box>
  );
};

export default LoginLayout;
